import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, Paper, Grid, Typography, FormControl, InputLabel, 
  Select, MenuItem, Button, CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Stack, IconButton, Container, Card, CardContent, Menu, Divider
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  getFormattedTimetable, getDepartments, getSemesters,
  deleteTimetableSlot, deleteSectionTimetable 
} from '../services/api';
import PageHeader from '../components/PageHeader';
import DeleteIcon from '@mui/icons-material/Delete';
// import GetAppIcon from '@mui/icons-material/GetApp'; // Removed unused import
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Helper function to format time strings in 12-hour format
const formatTime = (timeString) => {
  if (!timeString) return '';
  
  // Handle different time formats
  if (typeof timeString === 'string') {
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      let hour = parseInt(parts[0], 10);
      const minutes = parts[1].padStart(2, '0');

      // Special handling: database stores afternoon as 01–04 for post-lunch slots
      if (hour >= 1 && hour <= 4) {
        hour += 12; // treat as PM
      }

      const period = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12; // convert to 12-hour clock
      
      return `${hour}:${minutes} ${period}`;
    }
    return timeString;
  } else if (timeString instanceof Date) {
    // If it's a Date object
    let hours = timeString.getHours();
    const minutes = timeString.getMinutes().toString().padStart(2, '0');
    const period = hours >= 12 ? 'PM' : 'AM';
    
    // Convert hour to 12-hour format
    hours = hours % 12 || 12;
    
    return `${hours}:${minutes} ${period}`;
  }
  
  // Fallback
  return String(timeString);
};

// Helper function to convert time string to minutes for sorting
const timeToMinutes = (timeString) => {
  if (!timeString) return 0;
  
  // Handle different time formats
  if (typeof timeString === 'string') {
    // Split the time string and extract hours and minutes
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      let hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      
      // Convert to 24-hour format for proper sorting
      // Assume times after 7:00 and before 12:00 are AM, and times after 12:00 and before 7:00 are PM
      if (hours < 7) {
        // Early afternoon/evening hours (1:00-6:59) should be treated as PM (add 12 hours)
        hours += 12;
      }
      
      return hours * 60 + minutes;
    }
  } else if (timeString instanceof Date) {
    return timeString.getHours() * 60 + timeString.getMinutes();
  }
  
  return 0;
};

// Helper function to sort time slots
const sortTimeSlots = (slots) => {
  if (!slots || !Array.isArray(slots)) return [];
  
  return [...slots].sort((a, b) => {
    return timeToMinutes(a.start_time) - timeToMinutes(b.start_time);
  });
};

const Timetable = () => {
  const [departments, setDepartments] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [availableSections, setAvailableSections] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchDepartments();
    fetchSemesters();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await getDepartments();
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load departments',
        severity: 'error'
      });
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await getSemesters();
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load semesters',
        severity: 'error'
      });
    }
  };

  const handleViewTimetable = async () => {
    if (!selectedDepartment || !selectedSemester) {
      setSnackbar({
        open: true,
        message: 'Please select both department and semester',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Get the selected semester to check for sections
      const selectedSemesterObj = semesters.find(sem => sem.semester_id === parseInt(selectedSemester));
      
      // Extract sections from the semester
      let sections = [];
      if (selectedSemesterObj && selectedSemesterObj.section) {
        sections = selectedSemesterObj.section.split(',').filter(s => s.trim()).map(s => s.trim());
      }
      
      if (sections.length === 0) {
        sections = ['Default'];
      }
      
      setAvailableSections(sections);
      setSelectedSectionIndex(0);
      
      // Fetch the timetable
      const response = await getFormattedTimetable(selectedDepartment, selectedSemester);
      
      // Prioritize normalized_slots for section detection
      if (response.data.normalized_slots) {
        const sectionNames = Object.keys(response.data.normalized_slots);
        if (sectionNames.length > 0) {
          setAvailableSections(sectionNames);
          console.log('Using sections from normalized_slots:', sectionNames);
        }
      }
      // Fallback to section_data if normalized_slots is not available
      else if (response.data.section_data) {
        const sectionNames = Object.keys(response.data.section_data);
        if (sectionNames.length > 0) {
          setAvailableSections(sectionNames);
          console.log('Using sections from section_data:', sectionNames);
        }
      }
      // Fallback to sections array if provided by backend
      else if (response.data.sections && response.data.sections.length > 0) {
        setAvailableSections(response.data.sections);
        console.log('Using backend sections:', response.data.sections);
      }
      
      // Process and format the timetable data
      if (response.data && response.data.time_slots) {
        console.log('Original time slots:', JSON.stringify(response.data.time_slots));
        
        // Format time slots
        const formattedTimeSlots = response.data.time_slots.map(slot => {
          console.log('Processing slot:', slot);
          return {
            ...slot,
            start_time: formatTime(slot.start_time),
            end_time: formatTime(slot.end_time)
          };
        });
        
        console.log('Formatted time slots:', JSON.stringify(formattedTimeSlots));
        
        // Log time slot values in minutes for debugging
        formattedTimeSlots.forEach(slot => {
          console.log(`Time slot ${slot.start_time} - ${slot.end_time} converts to ${timeToMinutes(slot.start_time)} minutes`);
        });
        
        // Sort time slots
        const sortedTimeSlots = sortTimeSlots(formattedTimeSlots);
        
        console.log('Sorted time slots:', JSON.stringify(sortedTimeSlots));
        console.log('Sorted time slots in order:');
        sortedTimeSlots.forEach(slot => {
          console.log(`${slot.start_time} - ${slot.end_time}`);
        });
        
        // Create the updated timetable object
        const updatedTimetable = { 
          ...response.data, 
          time_slots: sortedTimeSlots 
        };
        
        setTimetable(updatedTimetable);
      } else {
        setTimetable(response.data);
      }
      console.log('Timetable data:', response.data);
    } catch (error) {
      console.error('Error fetching timetable:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load timetable',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Export timetable as a styled PDF similar to Teacher Timetable
  const exportToPDF = () => {
    if (!timetable) return;

    const currentSection = availableSections[selectedSectionIndex] || 'Default';
    const departmentName = departments.find(d => d.department_id === parseInt(selectedDepartment))?.department_name || 'Department';
    const semesterObj = semesters.find(s => s.semester_id === parseInt(selectedSemester));
    const semesterName = semesterObj?.semester_name || 'Semester';
    const academicYear = semesterObj?.academic_year || '';

    const title = `Timetable – ${departmentName} / ${semesterName}${academicYear ? ` (${academicYear})` : ''}${availableSections.length>1?` – Sec ${currentSection}`:''}`;

    const doc = new jsPDF('l', 'mm', 'a4');
    doc.setFontSize(16);
    doc.text(title, doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

    const days = timetable.days; // array of day names
    const tableColumn = ['Time Slot', ...days];
    const tableRows = [];

    // helper to find class info
    const getSlotInfo = (dayId, timeSlotId) => {
      const sect = currentSection;
      // 1. normalized_slots
      if (timetable.normalized_slots && timetable.normalized_slots[sect]) {
        const key = `${dayId}_${timeSlotId}`;
        const s = timetable.normalized_slots[sect][key];
        if (s) return s;
      }
      // 2. section_data
      if (timetable.section_data && timetable.section_data[sect]) {
        const match = (timetable.section_data[sect].entries || []).find(e => String(e.day_id)===String(dayId) && String(e.time_slot_id)===String(timeSlotId));
        if (match) return match;
      }
      // 3. slots map
      if (timetable.slots) {
        const potentialKeys = [ `${dayId}_${timeSlotId}`, `${String(dayId)}_${String(timeSlotId)}` ];
        for (const k of potentialKeys) {
          const s = timetable.slots[k];
          if (s && (!s.section || s.section === sect)) return s;
        }
      }
      return null;
    };

    timetable.time_slots.forEach(ts => {
      // Break/Lunch rows
      if (ts.is_break) {
        const breakLabel = ts.break_type === 'Lunch' ? 'LUNCH BREAK' : 'SHORT BREAK';
        const merged = { content: breakLabel, colSpan: days.length, styles: { fontStyle: 'bold', halign:'center' } };
        const breakRow = [ `${ts.start_time} - ${ts.end_time}`, merged ];
        tableRows.push(breakRow);
        return;
      }
      
      const row = [ `${ts.start_time} - ${ts.end_time}` ];

      days.forEach((dayName, idx) => {
        const slot = getSlotInfo(idx+1, ts.id);
        let cell = '';
        if (slot) {
          if (!slot.section) slot.section = currentSection; // ensure section label exists

          const subj = slot.subject || slot.subject_name || '';
          const teachRaw = String(slot.teacher_name || slot.teacher || '').trim();
          const isAuto = teachRaw.toLowerCase().replace(/\s+/g, '') === 'autoscheduler';
          const teachLine = !isAuto && teachRaw ? `(${teachRaw})` : '';


          const cellText = [subj, teachLine].filter(Boolean).join('\n');
          const cellObj = { content: cellText };
          if(slot.is_lab) cellObj.lab = true;
          cell = cellObj;
        }
        row.push(cell);
      });

      tableRows.push(row);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 25,
      theme: 'grid',
      styles: { fontSize: 10, textColor: [45,45,45], fontStyle:'bold', cellPadding: 2.5, overflow: 'linebreak', valign: 'middle', halign: 'center' },
      columnStyles: { 0: { fontStyle: 'bold', halign: 'left', cellWidth: 30 } },
      headStyles: { fillColor: [25,118,210], textColor: 255, fontStyle: 'bold', fontSize:11 },
      alternateRowStyles: { fillColor: [245,245,245] },
      didParseCell: (hook) => {
        if (hook.section === 'body') {
          const rawStr = typeof hook.cell.raw === 'string' ? hook.cell.raw : (hook.cell.raw?.content || '');

          if (hook.column.index > 0 && (hook.cell.raw?.lab || false)) {
            hook.cell.styles.fillColor = [232, 244, 253];
          }

          if (rawStr.includes('LUNCH BREAK')) {
            hook.cell.styles.fillColor = [255, 243, 224];
            hook.cell.styles.fontStyle = 'bold';
          } else if (rawStr.includes('SHORT BREAK')) {
            hook.cell.styles.fillColor = [232, 245, 233];
            hook.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    const safe = (str) => str.replace(/[^a-z0-9]/gi,'_');
    const sectionPart = availableSections.length>1?`_Sec_${currentSection}`:'';
    const fileName = `${safe(departmentName)}_${safe(semesterName)}${sectionPart}.pdf`;
    doc.save(fileName);
  };

  // Export timetable as Excel (xlsx) with full styling using exceljs
  const exportToExcel = async () => {
    if (!timetable) return;

    const currentSection = availableSections[selectedSectionIndex] || 'Default';
    const departmentName = departments.find(d=>d.department_id===parseInt(selectedDepartment))?.department_name||'Department';
    const semesterName   = semesters.find(s=>s.semester_id===parseInt(selectedSemester))?.semester_name||'Semester';

    // Build Excel using exceljs
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Timetable');

    // Define columns with slightly narrower widths to reduce overall table width
    ws.columns = [{ header: 'Time Slot', key: 'time', width: 16 },
      ...timetable.days.map(day => ({ header: day, key: day, width: 26 }))];

    const borderStyle = { style: 'thin', color: { argb: 'FFB0B0B0' } };

    // Style header row (row 1)
    ws.getRow(1).eachCell(cell => {
      cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: borderStyle, left: borderStyle, right: borderStyle, bottom: borderStyle };
    });

    let rowIndex = 2;

    const getSlot = (dayId, tsId) => {
      const sect = currentSection;
      if (timetable.normalized_slots && timetable.normalized_slots[sect]) {
        return timetable.normalized_slots[sect][`${dayId}_${tsId}`];
      }
      if (timetable.slots) {
        const k = `${dayId}_${tsId}`;
        return timetable.slots[k];
      }
      return null;
    };

    timetable.time_slots.forEach(ts => {
      if (ts.is_break) {
        const breakType = ts.break_type === 'Lunch' ? 'LUNCH BREAK' : 'SHORT BREAK';
        ws.addRow([`${ts.start_time} - ${ts.end_time}`, breakType]);
        ws.mergeCells(rowIndex, 2, rowIndex, timetable.days.length + 1);
        const row = ws.getRow(rowIndex);
        row.height = 30; // Allow more room for break/lunch label
        row.eachCell(cell => {
          cell.font = { bold: true };
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ts.break_type === 'Lunch' ? 'FFFFF3E0' : 'FFE8F5E9' } };
          cell.border = { top: borderStyle, left: borderStyle, right: borderStyle, bottom: borderStyle };
        });
        rowIndex++;
      return;
    }

      const rowData = [];
      const labFlags = [];
      rowData.push(`${ts.start_time} - ${ts.end_time}`);

      timetable.days.forEach((d, idxDay) => {
        const slot = getSlot(idxDay + 1, ts.id);
        if (slot) {
          const subj = slot.subject || slot.subject_name || '';
          const teachRaw = String(slot.teacher_name || slot.teacher || '').trim();
          const isAuto = teachRaw.toLowerCase().replace(/\s+/g, '') === 'autoscheduler';
          rowData.push(isAuto ? subj : `${subj}\n(${teachRaw})`);
          labFlags.push(slot.is_lab || false);
        } else {
          rowData.push('');
          labFlags.push(false);
        }
      });

      ws.addRow(rowData);
      const row = ws.getRow(rowIndex);
      row.height = 36; // slightly taller for better text fit
      // zebra stripe
      if (rowIndex % 2 === 0) {
        row.eachCell(cell => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FA' } };
        });
      }
      // style each cell
      row.eachCell((cell, colNumber) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        // bold time column
        if (colNumber === 1) {
          cell.font = { bold: true, size: 12 };
        }
        // lab shading
        if (colNumber > 1 && labFlags[colNumber - 2]) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE7F6' } };
        }
        // add borders
        cell.border = { top: borderStyle, left: borderStyle, right: borderStyle, bottom: borderStyle };
      });

      rowIndex++;
    });

    // Page setup
    ws.pageSetup = {
      orientation: 'landscape',
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 1,
      horizontalCentered: true
    };

    const safe = s => s.replace(/[^a-z0-9]/gi, '_');
    const sectionPart = availableSections.length > 1 ? `_Sec_${currentSection}` : '';
    const fname = `${safe(departmentName)}_${safe(semesterName)}${sectionPart}.xlsx`;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fname);

    setSnackbar({ open: true, message: 'Timetable exported to Excel', severity: 'success' });
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await deleteTimetableSlot(slotId);
      
      // Remove the slot from the displayed timetable
      const updatedSlots = { ...timetable.slots };
      for (const key in updatedSlots) {
        if (updatedSlots[key].id === slotId) {
          delete updatedSlots[key];
          break;
        }
      }
      
      setTimetable({ ...timetable, slots: updatedSlots });
      
      setSnackbar({
        open: true,
        message: 'Timetable slot deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting timetable slot:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete timetable slot',
        severity: 'error'
      });
    }
  };

  // Delete the entire timetable for the currently selected section
  const handleDeleteSectionTimetable = async () => {
    if (!selectedDepartment || !selectedSemester) {
      setSnackbar({
        open: true,
        message: 'Department and semester must be selected',
        severity: 'warning'
      });
      return;
    }

    const currentSection = availableSections[selectedSectionIndex] || 'Default';

    if (!window.confirm(`Are you sure you want to delete the timetable for Section ${currentSection}?`)) {
      return;
    }

    try {
      setLoading(true);

      await deleteSectionTimetable({
        department_id: selectedDepartment,
        semester_id: selectedSemester,
        section: currentSection
      });

      // Refresh timetable after deletion
      await handleViewTimetable();

      setSnackbar({
        open: true,
        message: `Timetable for section ${currentSection} deleted successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting timetable:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete timetable',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderTimetable = () => {
    if (!timetable) return null;

    // Get the selected department and semester names for display
    const selectedDepartmentObj = departments.find(dept => dept.department_id === parseInt(selectedDepartment));
    const selectedSemesterObj = semesters.find(sem => sem.semester_id === parseInt(selectedSemester));
    
    const currentSection = availableSections[selectedSectionIndex] || 'Default';
    
    const getSubjectColor = (subjectName, isLab = false) => {
      if (!subjectName) return '#f5f5f5';
      
      // For lab subjects, use a special color scheme
      if (isLab) {
        // Simple hash function to generate a consistent color from a string
        let hash = 0;
        for (let i = 0; i < subjectName.length; i++) {
          hash = subjectName.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        // Lab subjects get a purple-tinted palette
        const h = 270 + (Math.abs(hash % 50) - 25); // Range around purple (270)
        const s = '30%';
        const l = '92%';
        
        return `hsl(${h}, ${s}, ${l})`;
      } else {
        // Regular subjects
        let hash = 0;
        for (let i = 0; i < subjectName.length; i++) {
          hash = subjectName.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        // Ensure some saturation and lightness for readability
        const h = Math.abs(hash % 360);
        const s = '70%';
        const l = '85%';
        
        return `hsl(${h}, ${s}, ${l})`;
      }
    };
    
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Timetable for {selectedDepartmentObj?.department_name || 'Department'} - {selectedSemesterObj?.semester_name || 'Semester'} 
            ({selectedSemesterObj?.academic_year || ''})
            {availableSections.length > 1 && ` - Section ${currentSection}`}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Export & Delete buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={exportToPDF}
                  size="small"
                  sx={{
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  PDF
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<TableChartIcon />}
                  onClick={exportToExcel}
                  size="small"
                  sx={{
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  Excel
                </Button>
              </motion.div>

              {/* Delete timetable button */}
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSectionTimetable}
                  size="small"
                  sx={{
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(255,0,0,0.1)'
                    }
                  }}
                >
                  Delete
                </Button>
              </motion.div>
            </Box>
            
            {/* Section selector */}
            {availableSections.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 1 }}>
                  Section:
                </Typography>
                <Stack direction="row" spacing={1}>
                  {availableSections.map((section, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.95 }}
                      animate={{ 
                        scale: [1, idx === selectedSectionIndex ? 1.1 : 1, 1],
                        transition: { duration: idx === selectedSectionIndex ? 0.3 : 0 }
                      }}
                    >
                      <Chip
                        label={section}
                        color={idx === selectedSectionIndex ? "primary" : "default"}
                        variant={idx === selectedSectionIndex ? "filled" : "outlined"}
                        onClick={() => setSelectedSectionIndex(idx)}
                        size="small"
                        sx={{ 
                          cursor: 'pointer',
                          boxShadow: idx === selectedSectionIndex ? '0 2px 5px rgba(25, 118, 210, 0.3)' : 'none'
                        }}
                      />
                    </motion.div>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
        
        {/* Wrap the timetable in AnimatePresence for smooth section transitions */}
        <Box sx={{ position: 'relative', width: '100%' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedSectionIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4 }}
              style={{ width: '100%' }}
            >
            {/* Mobile-friendly horizontal scroll wrapper */}
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, minWidth: 650 }}>
                <Table>
                <TableHead>
                  <TableRow component={motion.tr} sx={{ bgcolor: 'primary.main' }}>
                    <TableCell 
                  component={motion.th}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  sx={{ fontWeight: 'bold', width: '100px', color: 'white', borderRight: '1px solid rgba(255,255,255,0.2)' }}
                >
                  Time / Day
                </TableCell>
                {timetable.days.map((day, index) => (
                  <TableCell 
                    component={motion.th}
                    key={index} 
                    align="center" 
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    sx={{ fontWeight: 'bold', color: 'white', borderRight: index < timetable.days.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none' }}
                  >
                    {day}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {timetable.time_slots.map((timeSlot, timeIndex) => {
                // Check if this is a break slot
                const isBreak = timeSlot.is_break;
                const breakType = timeSlot.break_type;
                const isLunchBreak = breakType === 'Lunch';
                const isShortBreak = breakType === 'Short Break';
                
                return (
                <TableRow
                  component={motion.tr}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.4, 
                    delay: timeIndex * 0.05, 
                    ease: 'easeOut'
                  }}
                  key={timeSlot.id} 
                  sx={{ 
                    bgcolor: isLunchBreak ? 'rgba(255, 243, 224, 0.8)' : 
                            isShortBreak ? 'rgba(232, 245, 233, 0.8)' : 
                            timeIndex % 2 === 0 ? 'background.paper' : 'rgba(0,0,0,0.02)',
                    '&:hover': { bgcolor: isBreak ? undefined : 'rgba(0,0,0,0.04)' },
                    height: isBreak ? '35px' : 'auto'
                  }}>
                  <TableCell 
                    component="th" 
                    scope="row" 
                    sx={{ 
                      whiteSpace: 'nowrap', 
                      fontWeight: isBreak ? 'bold' : 'medium', 
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      bgcolor: isBreak ? (isLunchBreak ? 'rgba(255, 243, 224, 0.9)' : 'rgba(232, 245, 233, 0.9)') : 'rgba(0,0,0,0.03)',
                      color: isBreak ? (isLunchBreak ? 'warning.dark' : 'success.dark') : 'inherit'
                    }}
                  >
                    {isBreak ? (
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                          {isLunchBreak ? '' : ''} {timeSlot.start_time} - {timeSlot.end_time}
                      </Typography>
                    ) : (
                      `${timeSlot.start_time} - ${timeSlot.end_time}`
                    )}
                  </TableCell>
                  {/* Map through days to create day columns */}
                  {timetable.days.map((day, dayIndex) => {
                    const dayId = dayIndex + 1;
                    
                    // If this is a break row, go back to spanning across all columns
                    // This creates a cleaner break display
                    if (isBreak) {
                      if (dayIndex === 0) {
                        return (
                          <TableCell 
                            key={dayIndex} 
                            align="center" 
                            colSpan={timetable.days.length} // Span all columns
                            sx={{ 
                              p: 0.5, 
                              borderRight: '1px solid rgba(224, 224, 224, 1)',
                              height: '35px',
                              bgcolor: isLunchBreak ? 'rgba(255, 243, 224, 0.7)' : 'rgba(232, 245, 233, 0.7)'
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%'
                              }}
                            >
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  color: isLunchBreak ? 'warning.dark' : 'success.dark', 
                                  fontWeight: 'bold'
                                }}
                              >
                                {isLunchBreak ? 'LUNCH BREAK' : 'SHORT BREAK'}
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      } else {
                        // For other days, return null to skip rendering
                        return null;
                      }
                    }
                    
                    // Current section being viewed
                    const currentSection = availableSections[selectedSectionIndex] || 'Default';
                    
                    // Try different key formats to find the slot
                    const keyFormats = [
                      `${dayId}_${timeSlot.id}`,
                      `${dayId}_${String(timeSlot.id)}`,
                      `${String(dayId)}_${timeSlot.id}`,
                      `${String(dayId)}_${String(timeSlot.id)}`
                    ];
                    
                    let exactSlot = null;
                    
                    // Check all possible key formats
                    for (const keyFormat of keyFormats) {
                      if (timetable.slots && timetable.slots[keyFormat]) {
                        const slotFromKey = timetable.slots[keyFormat];
                        // Only use this slot if it's for the current section
                        if (!slotFromKey.section || slotFromKey.section === currentSection) {
                          exactSlot = slotFromKey;
                          break;
                        }
                      }
                    }
                    
                    // Simplified slot search with less console logging
                    if (!exactSlot && timetable.slots) {
                      // Find matching slots by attributes rather than keys
                      Object.entries(timetable.slots).forEach(([key, slot]) => {
                        if (slot && 
                            slot.section === currentSection && 
                            String(slot.day_id || key.split('_')[0]) === String(dayId) && 
                            String(slot.time_slot_id || key.split('_')[1]) === String(timeSlot.id)) {
                          exactSlot = slot;
                        }
                      });
                    }
                      
                    // Also get section-specific slot from section_data if available
                    let sectionSlot = null;
                    if (timetable.section_data && timetable.section_data[currentSection]) {
                      // Look for matching slot in section_data
                      const sectionEntries = timetable.section_data[currentSection].entries || [];
                      
                      // Try exact match first
                      sectionSlot = sectionEntries.find(entry => 
                        parseInt(entry.day_id) === dayId && parseInt(entry.time_slot_id) === parseInt(timeSlot.id)
                      );
                      
                      // If no exact match, look more broadly by converting types
                      if (!sectionSlot) {
                        sectionSlot = sectionEntries.find(entry => {
                          const entryDayId = String(entry.day_id);
                          const entryTimeSlotId = String(entry.time_slot_id);
                          const thisDayId = String(dayId);
                          const thisTimeSlotId = String(timeSlot.id);
                          
                          return entryDayId === thisDayId && entryTimeSlotId === thisTimeSlotId;
                        });
                      }
                    }
                    
                    // Simplified normalized_slots lookup
                    let directSectionSlot = null;
                    if (timetable.normalized_slots && 
                        timetable.normalized_slots[currentSection]) {
                      
                      // Use a consistent key format for lookup
                      const normalizedKey = `${String(dayId)}_${String(timeSlot.id)}`;
                      directSectionSlot = timetable.normalized_slots[currentSection][normalizedKey];
                      
                      // If not found with the standard key format, try attribute-based lookup
                      if (!directSectionSlot) {
                        Object.values(timetable.normalized_slots[currentSection] || {}).forEach(slot => {
                          if (slot && 
                              String(slot.day_id) === String(dayId) && 
                              String(slot.time_slot_id) === String(timeSlot.id)) {
                            directSectionSlot = slot;
                          }
                        });
                      }
                    }
                    
                    // Simplified slot prioritization
                    const effectiveSlot = directSectionSlot || sectionSlot || exactSlot || null;
                    
                    // Always normalize field names for consistency
                    if (effectiveSlot) {
                      // Ensure section is set
                      effectiveSlot.section = effectiveSlot.section || currentSection;
                      
                      // Normalize field names
                      if (effectiveSlot.subject_name && !effectiveSlot.subject) {
                        effectiveSlot.subject = effectiveSlot.subject_name;
                      }
                      if (effectiveSlot.teacher_name && !effectiveSlot.teacher) {
                        effectiveSlot.teacher = effectiveSlot.teacher_name;
                      }
                      
                      // Set lab flag based on multiple indicators
                      // Check if it's marked as a lab from the backend
                      if (effectiveSlot.is_lab === undefined) {
                        // If the is_lab flag isn't set, try to detect lab subjects by their code or name
                        const subjectCode = effectiveSlot.subject_code || '';
                        const subjectName = effectiveSlot.subject || effectiveSlot.subject_name || '';
                        
                        // Specific subjects known not to be labs (overrides detection)
                        const notLabSubjects = ['Machine Learning', 'ML', 'DBMS', 'Algorithm Design and Analysis', 'ADA'];
                        
                        if (notLabSubjects.includes(subjectName) || notLabSubjects.includes(subjectCode)) {
                          effectiveSlot.is_lab = false;
                        } else {
                          // Lab subjects detection with stricter patterns to avoid false positives
                          effectiveSlot.is_lab = (
                            // Code pattern checks - more precise
                            (subjectCode.endsWith('LAB')) || 
                            (subjectCode.includes('BCSL')) || 
                            
                            // Name pattern checks - more precise
                            (subjectName.toLowerCase().endsWith('lab')) ||
                            (subjectName.toLowerCase().includes('practical')) ||
                            
                            // Special cases
                            (subjectName === 'LateXLAB') ||
                            (subjectCode === 'BCSL406')
                          );
                        }
                        
                        // Debug information
                        if (effectiveSlot.is_lab) {
                          console.log(`Detected lab subject: ${subjectName} (${subjectCode})`);
                        } else {
                          // Debug non-lab subjects
                          console.log(`Regular subject: ${subjectName} (${subjectCode})`);
                        }
                      }
                      
                      // Check if this is part of a 2-hour lab session
                      if (effectiveSlot.is_lab_session) {
                        effectiveSlot.is_lab = true;
                      }
                    }
                    
                    // Simpler logic to determine whether to show this slot
                    const showSlot = !effectiveSlot || effectiveSlot.section === currentSection;
                    
                    return (
                      <TableCell 
                        key={dayIndex} 
                        align="center" 
                        sx={{ 
                          p: 1, 
                          borderRight: dayIndex < timetable.days.length - 1 ? '1px solid rgba(224, 224, 224, 1)' : 'none'
                        }}
                      >
                        {showSlot && effectiveSlot ? (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                            whileHover={{ scale: 1.02, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                            style={{
                              backgroundColor: getSubjectColor(effectiveSlot.subject || effectiveSlot.subject_name, effectiveSlot.is_lab),
                              borderRadius: '8px',
                              padding: '12px',
                              height: '100%',
                              position: 'relative',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {effectiveSlot.is_lab_continuation ? (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                              >
                                <Typography variant="body2" sx={{ 
                                  fontStyle: 'italic', 
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  Lab Session Continued
                                </Typography>
                              </motion.div>
                            ) : (
                              <motion.div
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                              >
                                <Typography variant="subtitle2" sx={{ 
                                  fontWeight: 'bold', 
                                  mb: 0.5, 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {effectiveSlot.subject || effectiveSlot.subject_name || ''}
                                  {effectiveSlot.is_lab && (
                                    <Chip 
                                      size="small" 
                                      label={effectiveSlot.is_theory_session ? "THEORY" : "LAB"} 
                                      color={effectiveSlot.is_theory_session ? "primary" : "secondary"} 
                                      sx={{ ml: 1, height: 20, fontSize: '0.6rem' }}
                                    />
                                  )}
                                </Typography>
                                  {(() => {
                                    const teachDisp = String(effectiveSlot.teacher || effectiveSlot.teacher_name || '').trim();
                                    if (!teachDisp || teachDisp.toLowerCase().includes('Auto Scheduler')) return null;
                                    return (
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                                        {teachDisp}
                                </Typography>
                                    );
                                  })()}
                              </motion.div>
                            )}
                            {/* Section information removed as requested */}                            
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 0.7 }}
                              whileHover={{ scale: 1.2, opacity: 1 }}
                              style={{
                                position: 'absolute',
                                top: 2,
                                right: 2
                              }}
                            >
                              <IconButton
                                size="small"
                                color="error"
                                sx={{ 
                                  width: '28px',
                                  height: '28px',
                                  p: 0,
                                  '&:hover': {
                                    bgcolor: 'rgba(211, 47, 47, 0.1)'
                                  }
                                }}
                                onClick={() => handleDeleteSlot(effectiveSlot.id || effectiveSlot.timetable_slot_id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </motion.div>
                          </motion.div>
                        ) : null}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            </TableBody>
              </Table>
            </TableContainer>
            </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>
    );
  };

  const handleDepartmentChange = (event) => {
    const departmentId = event.target.value;
    setSelectedDepartment(departmentId);
    // Reset semester selection when department changes
    setSelectedSemester('');
  };


  const handleSemesterChange = (event) => {
    setSelectedSemester(event.target.value);
  };

  return (
    <Box>
      <PageHeader 
        title="View Timetable" 
        subtitle="View and manage the college timetable" 
      />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                label="Department"
                onChange={handleDepartmentChange}
              >
                {departments.map((department) => (
                  <MenuItem key={department.department_id} value={department.department_id}>
                    {department.department_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Semester</InputLabel>
              <Select
                value={selectedSemester}
                label="Semester"
                onChange={handleSemesterChange}
              >
                {semesters
                  .filter(semester => !selectedDepartment || semester.department_id === parseInt(selectedDepartment))
                  .map((semester) => (
                    <MenuItem key={semester.semester_id} value={semester.semester_id}>
                      {semester.semester_name} ({semester.academic_year})
                      {semester.section ? ` - ${semester.section.split(',').length} Sections` : ''}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleViewTimetable}
              disabled={loading}
            >
              View Timetable
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        renderTimetable()
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Timetable;
